import React from 'react';
import { Container, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import logo from '../assets/logo-mark.png';
import siteConfig, { services } from '../siteConfig';
import RateReviewIcon from '@material-ui/icons/RateReview';
import { useNavLinks } from './NavBar';
import { colors } from '../theme';

const useStyles = makeStyles((theme) => ({
  footer: {
    backgroundColor: colors.navy,
    color: 'rgba(255,255,255,0.75)',
    padding: theme.spacing(8, 0, 4),
    [theme.breakpoints.down('sm')]: {
      // Room for the floating call button on phones.
      paddingBottom: theme.spacing(12),
    },
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  logo: {
    width: 56,
    height: 56,
    borderRadius: '50%',
    border: '1px solid rgba(255,255,255,0.15)',
  },
  brandName: {
    fontFamily: theme.typography.h6.fontFamily,
    fontWeight: 800,
    fontSize: 18,
    color: '#FFFFFF',
    lineHeight: 1.25,
  },
  heading: {
    fontFamily: theme.typography.h6.fontFamily,
    fontWeight: 700,
    color: '#FFFFFF',
    fontSize: 15,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    marginBottom: theme.spacing(2),
  },
  list: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    '& li': { marginBottom: theme.spacing(1) },
    // Taller links on phones so they're easy to tap.
    [theme.breakpoints.down('sm')]: {
      '& li': { marginBottom: 0 },
    },
  },
  link: {
    color: 'rgba(255,255,255,0.75)',
    textDecoration: 'none',
    '&:hover': { color: colors.gold },
    [theme.breakpoints.down('sm')]: {
      display: 'inline-block',
      padding: '11px 0',
    },
  },
  phone: {
    display: 'inline-block',
    color: colors.gold,
    fontFamily: theme.typography.h6.fontFamily,
    fontWeight: 700,
    fontSize: 20,
    textDecoration: 'none',
    [theme.breakpoints.down('sm')]: {
      padding: '8px 0',
    },
  },
  reviewLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    marginTop: theme.spacing(2.5),
    padding: 0,
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(1),
      padding: '11px 0',
    },
    border: 0,
    background: 'none',
    cursor: 'pointer',
    font: 'inherit',
    textAlign: 'left',
    fontSize: 14,
    fontWeight: 600,
    color: '#FFFFFF',
    textDecoration: 'underline',
    textUnderlineOffset: '3px',
    '&:hover': { color: colors.gold },
    '& svg': { fontSize: 18, color: colors.gold },
  },
  bottom: {
    marginTop: theme.spacing(6),
    paddingTop: theme.spacing(3),
    borderTop: '1px solid rgba(255,255,255,0.12)',
    fontSize: 14,
    color: 'rgba(255,255,255,0.55)',
  },
}));

export default function Footer({ onLeaveReview }) {
  const classes = useStyles();
  const navLinks = useNavLinks();
  const year = new Date().getFullYear();

  return (
    <footer className={classes.footer}>
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <div className={classes.brand}>
              <img src={logo} alt="" className={classes.logo} />
              <span className={classes.brandName}>{siteConfig.businessName}</span>
            </div>
            <Typography variant="body2" style={{ color: 'inherit', maxWidth: 320 }}>
              Lawn care, landscaping and pressure washing at affordable prices. Free estimates, always.
            </Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <div className={classes.heading}>Services</div>
            <ul className={classes.list}>
              {services.map((service) => (
                <li key={service.title}>
                  <a href="#services" className={classes.link}>
                    {service.title}
                  </a>
                </li>
              ))}
            </ul>
          </Grid>
          <Grid item xs={6} md={2}>
            <div className={classes.heading}>Explore</div>
            <ul className={classes.list}>
              {navLinks.map((link) => (
                <li key={link.href}>
                  <a href={link.href} className={classes.link}>
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </Grid>
          <Grid item xs={12} md={3}>
            <div className={classes.heading}>Get in touch</div>
            <a href={siteConfig.phoneHref} className={classes.phone}>
              {siteConfig.phoneDisplay}
            </a>
            <Typography variant="body2" style={{ color: 'inherit', marginTop: 8 }}>
              {siteConfig.availability}
              {siteConfig.serviceArea ? <><br />Serving {siteConfig.serviceArea}</> : null}
            </Typography>
            {onLeaveReview && (
              <button type="button" className={classes.reviewLink} onClick={onLeaveReview}>
                <RateReviewIcon /> Happy with our work? Leave a review
              </button>
            )}
          </Grid>
        </Grid>
        <div className={classes.bottom}>
          © {year} {siteConfig.businessName}. All rights reserved.
        </div>
      </Container>
    </footer>
  );
}
